import { useContext, useEffect, useState } from 'react';
import batchTeacherStructureService from 'ApiService/Teacher/batch.teacher.service';
import { AuthContext } from 'contexts/AuthContext';
import toast from 'react-hot-toast';

const dayMap = {
  Monday: 'M', Tuesday: 'T', Wednesday: 'W', Thursday: 'Th',
  Friday: 'F', Saturday: 'S', Sunday: 'Su',
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const day = date.getDate().toString().padStart(2, '0');
  const month = date.toLocaleString('default', { month: 'short' });
  const year = date.getFullYear().toString().slice(-2);
  return `${day}${month}${year}`;
};

const formatSchedule = (daySchedules) => {
  if (!daySchedules || daySchedules.length === 0) return '';
  return daySchedules
    .map((s) => {
      const d = dayMap[s.day] || s.day;
      const t = s.startTime.replace(':00', '').replace(' ', '');
      return `${d}-${t}`;
    })
    .join(' | ');
};

const getBranchInitials = (branches) => {
  if (!branches || branches.length === 0) return '';
  return branches
    .map((br) => (br.name || '').split(',').pop().trim()[0] || '')
    .join('');
};

const getBatchDisplay = (batch) => {
  if (!batch) return '';
  const num = `B${batch.batchNumber}`;
  const date = formatDate(batch.startDate);
  const sched = formatSchedule(batch.daySchedules);
  const branch = getBranchInitials(batch.branches);
  let display = num;
  if (date) display += ` | ${date}`;
  if (sched) display += ` (${sched})`;
  if (branch) display += ` ${branch}`;
  return display;
};

const SelectClass = ({
  classData,
  setClassData,
  courses,
  loadingCourses,
  setDisable,
}) => {
  const { state } = useContext(AuthContext);
  const [batchStructures, setBatchStructures] = useState([]);
  const [loadingStructures, setLoadingStructures] = useState(false);
  const [batches, setBatches] = useState([]);
  const [loadingBatches, setLoadingBatches] = useState(false);
  const [errors] = useState([]);

  const handleCourseChange = (event) => {
    const courseId = event.target.value;
    const newClassData = { ...classData, courseId, structureId: '', batchId: '' };
    setClassData(newClassData);
    setBatchStructures([]);
    setBatches([]);
    setDisable(!(newClassData.courseId && newClassData.structureId && newClassData.batchId));
  };

  const handleStructureChange = (event) => {
    const structureId = event.target.value;
    const newClassData = { ...classData, structureId, batchId: '' };
    setClassData(newClassData);
    setBatches([]);
    setDisable(!(newClassData.courseId && newClassData.structureId && newClassData.batchId));
  };

  const handleBatchChange = (event) => {
    const batchId = event.target.value;
    const newClassData = { ...classData, batchId };
    setClassData(newClassData);
    setDisable(!(newClassData.courseId && newClassData.structureId && newClassData.batchId));
  };

  useEffect(() => {
    if (!classData.courseId) {
      setBatchStructures([]);
      return;
    }
    (async () => {
      setLoadingStructures(true);
      try {
        const res = await batchTeacherStructureService.getBatchStructuresByCourse(classData.courseId);
        if (res.data.success) {
          setBatchStructures(res.data.batchStructures || []);
        } else {
          toast.error(res.data.message || 'Failed to load batch structures.');
        }
      } catch (err) {
        toast.error(err.response?.data?.message || 'Failed to load batch structures.');
      } finally {
        setLoadingStructures(false);
      }
    })();
  }, [classData.courseId]);

  useEffect(() => {
    if (!classData.courseId || !classData.structureId) {
      setBatches([]);
      return;
    }
    (async () => {
      setLoadingBatches(true);
      try {
        const selectedBranch = state?.user?.branch?._id || state?.user?.branch?.name;
        const res = await batchTeacherStructureService.getBatchesByCourseStructure(
          classData.courseId,
          classData.structureId,
          selectedBranch
        );
        if (res.data.success) {
          setBatches(res.data.batches || []);
        } else {
          toast.error(res.data.message || 'Failed to load batches.');
        }
      } catch (err) {
        toast.error(err.response?.data?.message || 'Failed to load batches.');
      } finally {
        setLoadingBatches(false);
      }
    })();
  }, [classData.courseId, classData.structureId, state?.user?.branch]);

  return (
    <div className="w-full rounded-lg">
      <div className="flex justify-center">
        <h4 className="text-lg font-bold text-navy-700 dark:text-white">
          Select Class Information
        </h4>
      </div>

      <div className="mt-6 flex flex-col md:flex-row md:items-center md:justify-center md:gap-6">
        <div className="w-full md:w-1/2">
          <label className="ml-3 text-sm font-bold text-navy-700 dark:text-white">
            Select Course
          </label>
          <select
            onChange={handleCourseChange}
            value={classData?.courseId}
            name="course"
            className="mt-2 w-full cursor-pointer rounded-xl border border-gray-200 py-3 pl-2.5 pr-3 text-sm font-normal outline-none dark:!border-white/10 dark:!bg-navy-800 dark:text-white"
          >
            <option value="">Click here to Select Course</option>
            {loadingCourses ? (
              <option>Loading courses...</option>
            ) : (
              courses.map((course, i) => (
                <option key={course._id} value={course._id}>
                  {i + 1}. {course.courseName}
                </option>
              ))
            )}
          </select>
        </div>

        <div className="w-full md:w-1/2">
          <label className="ml-3 text-sm font-bold text-navy-700 dark:text-white">
            Select Batch Structure
          </label>
          <select
            value={classData?.structureId || ''}
            onChange={handleStructureChange}
            disabled={!classData.courseId}
            className="mt-2 w-full cursor-pointer rounded-xl border border-gray-200 py-3 pl-2.5 pr-3 text-sm font-normal outline-none dark:!border-white/10 dark:!bg-navy-800 dark:text-white disabled:opacity-50"
          >
            <option value="">Select Structure</option>
            {loadingStructures ? (
              <option>Loading...</option>
            ) : batchStructures.length === 0 ? (
              <option>No structures available</option>
            ) : (
              batchStructures.map((bs) => (
                <option key={bs._id} value={bs._id}>
                  {bs.name}
                </option>
              ))
            )}
          </select>
        </div>

        <div className="w-full md:w-1/2">
          <label className="ml-3 text-sm font-bold text-navy-700 dark:text-white">
            Select Batch
          </label>
          <select
            onChange={handleBatchChange}
            value={classData?.batchId}
            name="batch"
            disabled={!classData.structureId}
            className="mt-2 w-full cursor-pointer rounded-xl border border-gray-200 py-3 pl-2.5 pr-3 text-sm font-normal outline-none dark:!border-white/10 dark:!bg-navy-800 dark:text-white disabled:opacity-50"
          >
            <option value="">Click here to Select Batch</option>
            {loadingBatches ? (
              <option>Loading batches...</option>
            ) : batches.length > 0 ? (
              batches.map((batch) => (
                <option key={batch._id} value={batch._id}>
                  {getBatchDisplay(batch)}
                </option>
              ))
            ) : (
              <option value="">No batches available</option>
            )}
          </select>
        </div>
      </div>

      {errors.length > 0 && (
        <div className="text-red-500">
          {errors.map((error, index) => (
            <p key={index}>{error}</p>
          ))}
        </div>
      )}
    </div>
  );
};

export default SelectClass;